import { combineLatest, defer } from 'rxjs';
import { lightFormat } from 'date-fns';

import { poll, Properties, Reservations, Unsubscribe, unsubscribeCallback } from '@lib/state';
import { RoomCountStore, roomCountStore } from './room-count.store';
import { mapRoomCount, roomCountRequestKey } from './room-count.model';
import { RoomCountType } from '@lib/state/api/generated/properties';

export class RoomCountService {
  constructor(
    private readonly store: RoomCountStore,
    private readonly propertiesRoomCountApi: Properties.RoomCountsApi,
    private readonly reservationsRoomCountApi: Reservations.RoomCountsApi
  ) {}

  pollRoomCounts(
    propertyId: string,
    startDate: Date,
    endDate: Date,
    countBy: RoomCountType,
    roomTypeId?: string,
    interval = 30000
  ): Unsubscribe {
    const start = lightFormat(startDate, 'yyyy-MM-dd');
    const end = lightFormat(endDate, 'yyyy-MM-dd');
    const requestKey = roomCountRequestKey(propertyId, startDate, endDate, countBy, roomTypeId);

    // a new poll means a new property, range, count type or filter: clear the previous figures so the
    // chart never shows them beside figures computed under the new setting
    this.store.remove();

    const sub = defer(() =>
      combineLatest([
        this.getRoomCountsRequest(propertyId, start, end, countBy, roomTypeId),
        this.getReservationCountsRequest(propertyId, start, end, countBy, roomTypeId),
      ])
    )
      .pipe(poll(interval))
      .subscribe(([propertyCounts, reservationCounts]) => {
        const lookup = new Map<string, Reservations.RoomCountModel>(
          reservationCounts.map(x => [x.date, x])
        );
        // replace rather than upsert, so every column comes from the same pair of responses
        this.store.set(
          propertyCounts.map(propertyCount => {
            const reservationCount = lookup.get(propertyCount.date) ?? {
              date: propertyCount.date,
              reservedRooms: 0,
              checkInCount: 0,
              checkOutCount: 0,
              totalRooms: 0,
              readyRooms: 0,
              inventoryAdjustments: 0,
              openReservations: 0,
            };
            return mapRoomCount(propertyId, propertyCount, reservationCount, requestKey);
          })
        );
      });

    return unsubscribeCallback(sub);
  }

  updateIntervalFilter(start: Date, end: Date) {
    this.store.update(() => ({
      ui: {
        start,
        end,
      },
    }));
  }

  private async getRoomCountsRequest(
    propertyId: string,
    start: string,
    end: string,
    countBy: RoomCountType,
    roomTypeId?: string
  ) {
    const response = await this.propertiesRoomCountApi.propertyIdRoomcountsGet(
      propertyId,
      start,
      end,
      roomTypeId,
      countBy
    );
    return response.data.data;
  }

  private async getReservationCountsRequest(
    propertyId: string,
    start: string,
    end: string,
    countBy: RoomCountType,
    roomTypeId?: string
  ) {
    const response = await this.reservationsRoomCountApi.roomcountsGet(
      propertyId,
      start,
      end,
      roomTypeId,
      countBy
    );
    return response.data.data;
  }
}

export const roomCountService = new RoomCountService(
  roomCountStore,
  new Properties.RoomCountsApi(),
  new Reservations.RoomCountsApi()
);
